import { LoadingLayout } from 'components';
import { useScrollToTop } from 'hooks';
import { ErrorFallback } from 'modules/App/ErrorFallback';
import { LegalClientAddBeneficiaryDialogFeature } from 'modules/client/legalClientAddBeneficiary';
import { LegalClientAddBranchDialogFeature } from 'modules/client/legalClientAddBranch';
import { LegalClientAddHeadDialogFeature } from 'modules/client/legalClientAddHead/dialogs';
import { LegalClientAddLegalHeadDialogFeature } from 'modules/client/legalClientAddLegalHead';

import { getLegalClientMainInfo } from '../helpers';
import { useAddDialogs, useLegalClient, useLegalClientStatus } from '../hooks';
import { LegalClientView } from '../views';

export const LegalClientContainer = () => {
  const {
    legalClient,
    loading,
    countries,
    countriesLoading,
    error,
    initialValues,
    submitLoading,
    formError,
    breadcrumbsTitles,
    managers,
    managersLoading,
    isEditablePersonalAccountNumber,
    deleteLegalClientLoading,
    onSubmit,
    onClickBranch,
    lockClientIfPossible,
    unlockClient,
    onShowVersionHistory,
    refetchLegalClient,
    onDeleteBeneficiary,
    onDeleteHead,
    onDeleteLegalHead,
    onAddBody,
    onUpdateBody,
    onArchiveBody,
    onDearchiveBody,
    onDeleteBody,
    onArchiveHead,
    onArchiveLegalHead,
    onDearchiveHead,
    onDearchiveLegalHead,
    onArchiveBeneficiary,
    onDearchiveBeneficiary,
    onArchiveBranch,
    onDearchiveBranch,
    onDeleteBranch,
    onLinkToBright,
    onDownloadLegalClientQuestionnaire,
    onDeleteLegalClient,
    onClickRepresentative,
    onClickHead,
    onClickLegalHead,
    onClickBeneficiary,
  } = useLegalClient();

  const {
    fetchAvailableStatuses: fetchStatusesForChange,
    loading: loadingStatuses,
    submitLoading: submitLoadingStatus,
    onChangeOnboardingStatus,
    statuses: statusesForChange,
    onChangeAccountStatus,
    visibleOnboardingStatuses,
  } = useLegalClientStatus(legalClient);

  const {
    dialogAddBeneficiaryDialogProps,
    dialogAddHeadDialogProps,
    dialogAddLegalHeadDialogProps,
    dialogAddBranchDialogProps,

    onClickAddBeneficiary,
    onClickAddHead,
    onClickAddLegalHead,
    onClickAddBranch,
    onSuccessAddBeneficiary,
    onSuccessAddHead,
    onSuccessAddLegalHead,
    onSuccessAddBranch,
  } = useAddDialogs(refetchLegalClient);

  useScrollToTop([]);

  if (error || (!legalClient && !loading)) {
    return <ErrorFallback />;
  }

  if (loading || !legalClient) {
    return <LoadingLayout />;
  }

  const mainInfo = getLegalClientMainInfo(legalClient);

  return (
    <>
      <LegalClientView
        beneficiaries={legalClient.beneficiaries ?? []}
        bodies={legalClient.bodies ?? []}
        branches={legalClient.branches ?? []}
        breadcrumbsTitles={breadcrumbsTitles}
        countries={countries}
        countriesLoading={countriesLoading}
        currentAccountStatus={legalClient.accountStatus}
        currentOnboardingStatus={legalClient.onboardingStatus}
        deleteLegalClientLoading={deleteLegalClientLoading}
        fetchStatusesForChange={fetchStatusesForChange}
        hasBranches={!!legalClient.branches?.length}
        heads={legalClient.heads ?? []}
        initialErrors={formError}
        initialValues={initialValues}
        isEditablePersonalAccountNumber={isEditablePersonalAccountNumber}
        legalHeads={legalClient.legalHeads ?? []}
        loadingStatuses={loadingStatuses}
        lockClientIfPossible={lockClientIfPossible}
        mainInfo={mainInfo}
        managers={managers}
        managersLoading={managersLoading}
        representativeInfo={legalClient.representative}
        representativeOnboardingData={legalClient.representativeOnboarding}
        statusesForChange={statusesForChange}
        submitLoading={submitLoading}
        submitLoadingStatus={submitLoadingStatus}
        unidentifiableBeneficiaryLetterFiles={
          legalClient.unidentifiableBeneficiaryLetterFiles
        }
        unlockClient={unlockClient}
        visibleOnboardingStatuses={visibleOnboardingStatuses}
        onAddBody={onAddBody}
        onArchiveBeneficiary={onArchiveBeneficiary}
        onArchiveBody={onArchiveBody}
        onArchiveBranch={onArchiveBranch}
        onArchiveHead={onArchiveHead}
        onArchiveLegalHead={onArchiveLegalHead}
        onChangeAccountStatus={onChangeAccountStatus}
        onChangeOnboardingStatus={onChangeOnboardingStatus}
        onClickAddBeneficiary={onClickAddBeneficiary}
        onClickAddBranch={onClickAddBranch}
        onClickAddHead={onClickAddHead}
        onClickAddLegalHead={onClickAddLegalHead}
        onClickBeneficiary={onClickBeneficiary}
        onClickBranch={onClickBranch}
        onClickHead={onClickHead}
        onClickLegalHead={onClickLegalHead}
        onClickRepresentative={onClickRepresentative}
        onDearchiveBeneficiary={onDearchiveBeneficiary}
        onDearchiveBody={onDearchiveBody}
        onDearchiveBranch={onDearchiveBranch}
        onDearchiveHead={onDearchiveHead}
        onDearchiveLegalHead={onDearchiveLegalHead}
        onDeleteBeneficiary={onDeleteBeneficiary}
        onDeleteBody={onDeleteBody}
        onDeleteBranch={onDeleteBranch}
        onDeleteHead={onDeleteHead}
        onDeleteLegalClient={onDeleteLegalClient}
        onDeleteLegalHead={onDeleteLegalHead}
        onDownloadLegalClientQuestionnaire={onDownloadLegalClientQuestionnaire}
        onLinkToBright={onLinkToBright}
        onShowVersionHistory={onShowVersionHistory}
        onSubmit={onSubmit}
        onUpdateBody={onUpdateBody}
      />
      <LegalClientAddHeadDialogFeature
        legalClient={legalClient}
        onSuccess={onSuccessAddHead}
        {...dialogAddHeadDialogProps}
      />
      <LegalClientAddBeneficiaryDialogFeature
        legalClient={legalClient}
        onSuccess={onSuccessAddBeneficiary}
        {...dialogAddBeneficiaryDialogProps}
      />
      <LegalClientAddLegalHeadDialogFeature
        legalClient={legalClient}
        onSuccess={onSuccessAddLegalHead}
        {...dialogAddLegalHeadDialogProps}
      />
      <LegalClientAddBranchDialogFeature
        legalClient={legalClient}
        onSuccess={onSuccessAddBranch}
        {...dialogAddBranchDialogProps}
      />
    </>
  );
};
